import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, mergeMap } from "rxjs/operators";

import { AppState } from "../../reducers";
import * as SitesActionTypes from "../../actions/sites/sites.actions";
import * as ProductActionType from "../../actions/products/product.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

import { SitesService } from "src/app/shared/services/sites/sites.service";
import { WarehouseService } from "src/app/shared/services/warehouses/warehouses.service";
import { DispenserService } from "src/app/shared/services/dispensers/dispenser.service";
import { FccService } from "src/app/shared/services/ffc/fcc.service";
import { PosService } from "src/app/shared/services/pos/pos.service";
import { FuelPointService } from "src/app/shared/services/fuelPoints/fuelpoint.service";
import { PriceSignService } from "src/app/shared/services/price-signs/price-sign.service";
import { PeripheralsService } from "src/app/shared/services/peripherals/peripherals.serive";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { MultimediaService } from "src/app/shared/services/multimedia/peripherals.serive";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FieldType, FilterName } from "src/app/models/custom-table/custom-table.model";

@Injectable()
export class SitesEffects {
  // Get Sites List
  getSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitesList),
      exhaustMap((action) =>
        this.sitesService
          .getSites(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              SitesActionTypes.loadSitesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                SitesActionTypes.loadSitesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "SiteManagement.SitesLoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  // Get Sites Excel List
  getSitesExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitesExcelList),
      exhaustMap((action) =>
        this.sitesService
          .getSites(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              SitesActionTypes.loadSitesExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                SitesActionTypes.loadSitesExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "SiteManagement.SitesLoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Sites Table Config
  getSitesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
              SitesActionTypes.loadSitesTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                SitesActionTypes.loadSitesTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  // Save Sites Table Config
  saveSitesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.saveSitesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Categories for the table by Usage
  getCategoriesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getCategoriesForListByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              SitesActionTypes.loadCategoriesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "SiteManagement.loadCategoriesError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties for the table by Usage
  getPropertiesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getPropertiesForListByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) => {
              if (action.usageCode === "DEVICE_WAREHOUSE") {
                return SitesActionTypes.loadDeviceWarehousePropertiesForListByUsage({
                  response: result,
                })
              } 
              if (action.usageCode === "POS") {
                return SitesActionTypes.loadDevicePosPropertiesForListByUsage({
                  response: result,
                })
              } 
              return SitesActionTypes.loadPropertiesForListByUsage({
                response: result,
              })
            }),
            catchError((error) => {
              const actions = [
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "SiteManagement.loadPropertiesError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  //Get Site Regions
  getSitesRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteRegionsList),
      exhaustMap((action) =>
        this.sitesService.getSitesRegions(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteRegionsList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteRegionsList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadRegionError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  //Get Site Profiles
  getSitesProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteProfileList),
      exhaustMap((action) =>
        this.sitesService.getSitesProfile(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteProfileList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteProfileList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadProfileError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getEnabledSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getEnabledSitesList),
      exhaustMap((action) =>
        this.sitesService
          .getEnabledSites(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              SitesActionTypes.loadEnabledSitesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                SitesActionTypes.loadEnabledSitesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "SiteManagement.errorLoadEnabledSites",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  createSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createSite),
      exhaustMap((action) =>
        this.sitesService.createSite(action).pipe(
          mergeMap((result) => [
            SitesActionTypes.resetCreation(),
            SitesActionTypes.getSite({ code: result?.result?.code })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.SiteCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.SiteCreateFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateSite),
      exhaustMap((action) =>
        this.sitesService.updateSite(action.site).pipe(
          map((result) => {
            return SitesActionTypes.getSite({
              code: "" + result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.SiteUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.SiteUpdateFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSite),
      exhaustMap((action) =>
        this.sitesService.getSite(action.code).pipe(
          map((result) => SitesActionTypes.loadSite({ response: result })),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSite({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.SiteLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteWarehouse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteWarehouseList),
      exhaustMap((action) =>
        this.sitesService.getSiteWarehouses(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteWarehouseList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteWarehouseList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadWarehouseError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getWarehouseTypesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getWarehouseTypesList),
      exhaustMap((action) =>
        this.sitesService.getWarehouseTypes(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadWarehouseTypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadWarehouseTypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.warehouseTypesLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getPumpTypesList$ = createEffect(() => 
    this.actions$.pipe(
      ofType(SitesActionTypes.getPumpTypesList),
      exhaustMap((action) =>
        this.sitesService.getPumpTypes(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadPumpTypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadPumpTypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.pumpTypesLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getPriceSignTypesList$ = createEffect(() => 
    this.actions$.pipe(
      ofType(SitesActionTypes.getPriceSignTypesList),
      exhaustMap((action) =>
        this.sitesService.getPriceSignTypes(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadPriceSignTypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadPriceSignTypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.priceSignTypesLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getPosTypesList$ = createEffect(() => 
    this.actions$.pipe(
      ofType(SitesActionTypes.getPosTypesList),
      exhaustMap((action) =>
        this.sitesService.getPosTypes(action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadPosTypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadPosTypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.posTypesLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteDispenserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteDispenserList),
      exhaustMap((action) =>
        this.sitesService.getSiteDispensers(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteDispenserList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteDispenserList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.dispenserListLoadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteFccList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteFccList),
      exhaustMap((action) =>
        this.sitesService.getSiteFccs(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteFccList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteFccList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.fccLoadingError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSitePosList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitePosList),
      exhaustMap((action) =>
        this.sitesService.getSitePos(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSitePosList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSitePosList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadPosError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteFuelPointsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteFuelPointList),
      exhaustMap((action) =>
        this.sitesService.getSiteFuelpoints(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteFuelPointList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteFuelPointList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadFuelpoinsError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSitePriceSignsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitePriceSignList),
      exhaustMap((action) =>
        this.sitesService.getSitePriceSigns(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSitePriceSignList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSitePriceSignList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadPriceSignsError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getFccDeviceSubTypeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getFccDeviceSubtypesList),
      exhaustMap((action) =>
        this.sitesService.getDevicesSubTypes(action.pageReq, "FCC").pipe(
          map((result) =>
            SitesActionTypes.loadFccDeviceSubtypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadFccDeviceSubtypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadDeviceSubTypeError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteProductList),
      exhaustMap((action) =>
        this.sitesService.getSiteProductList(action.code).pipe(
          map((result) =>
            SitesActionTypes.loadSiteProductList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteProductList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadSiteProductListError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteFuelProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteFuelProductList),
      exhaustMap((action) =>
        this.sitesService.getSiteFuelProductList(action.code).pipe(
          map((result) =>
            SitesActionTypes.loadSiteFuelProductList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteFuelProductList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadSiteProductListError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteStoreProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteStoreProductList),
      exhaustMap((action) =>
        this.sitesService.getSiteStoreProductList(action.code).pipe(
          map((result) =>
            SitesActionTypes.loadSiteStoreProductList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteStoreProductList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadSiteProductListError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteGradeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteGradeList),
      exhaustMap((action) =>
        this.sitesService.getSiteGradeList(action.code).pipe(
          map((result) =>
            SitesActionTypes.loadSiteGradeList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteGradeList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.loadSiteGradeListError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updatePos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updatePos),
      exhaustMap((action) =>
        this.posService.updatePos(action.pos).pipe(
          map((result) => {
            return SitesActionTypes.getSitePosList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PosUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PosUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateVPos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateVPos),
      exhaustMap((action) =>
        this.posService.updatePos(action.pos).pipe(
          map((result) => {
            return SitesActionTypes.getSitePosList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.VPosUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.VPosUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateWarehouse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateWarehouse),
      exhaustMap((action) =>
        this.warehouseService.updateWarehouse(action.warehouse).pipe(
          map((result) => {
            return SitesActionTypes.getSiteWarehouseList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.WarehouseSiteUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.WarehouseSiteUpdateFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateDispenser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateDispenser),
      exhaustMap((action) =>
        this.dispenserService.updateDispenser(action.dispenser).pipe(
          map((result) => {
            return SitesActionTypes.getSiteDispenserList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.DispenserUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.DispenserFailedUpdated",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateFcc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateFcc),
      exhaustMap((action) =>
        this.fccService.updateFcc(action.fcc).pipe(
          map((result) => {
            return SitesActionTypes.getSiteFccList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.FccUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.FccUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createWarehouse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createWarehouse),
      exhaustMap((action) =>
        this.warehouseService.createWarehouse(action.warehouse).pipe(
          map((result) => {
            return SitesActionTypes.getSiteWarehouseList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.WarehouseCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.WarehouseCreatedFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createDispenser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createDispenser),
      exhaustMap((action) =>
        this.dispenserService.createDispenser(action.dispenser).pipe(
          map((result) => {
            return SitesActionTypes.getSiteDispenserList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.DispenserCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.DispenserErrorCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createPos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createPos),
      exhaustMap((action) =>
        this.posService.createPos(action.pos).pipe(
          map((result) => {
            return SitesActionTypes.getSitePosList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PosCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PosCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createVPos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createVPos),
      exhaustMap((action) =>
        this.posService.createPos(action.pos).pipe(
          map((result) => {
            return SitesActionTypes.getSitePosList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.VPosCreateSuccess",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.VPosCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createFcc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createFcc),
      exhaustMap((action) =>
        this.fccService.createFcc(action.fcc).pipe(
          map((result) => {
            return SitesActionTypes.getSiteFccList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.FccCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.FccCreateError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createFuelPoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createFuelPoint),
      exhaustMap((action) =>
        this.fuelPointService.createFuelPoint(action.fuelpoint).pipe(
          map((result) => {
            return SitesActionTypes.getSiteFuelPointList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.FuelPointCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.FuelPointFailedCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateFuelPoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateFuelPoint),
      exhaustMap((action) =>
        this.fuelPointService.updateFuelPoint(action.fuelpoint).pipe(
          map((result) => {
            return SitesActionTypes.getSiteFuelPointList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.FuelPointUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.FuelPointUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createPriceSign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createPriceSign),
      exhaustMap((action) =>
        this.priceSignService.createPriceSign(action.priceSign).pipe(
          map((result) => {
            return SitesActionTypes.getSitePriceSignList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PriceSignCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PriceSignFailedCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updatePriceSign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updatePriceSign),
      exhaustMap((action) =>
        this.priceSignService.updatePriceSign(action.priceSign).pipe(
          map((result) => {
            return SitesActionTypes.getSitePriceSignList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PriceSignUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PriceSignUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createProductList),
      exhaustMap((action) =>
        this.sitesService.createSiteProductList(action).pipe(
          map((result) => {
            return SitesActionTypes.getSiteProductList({ code: action.siteCode });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.siteProductListSaved",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.siteProductListSaveFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createFuelProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createFuelProductList),
      exhaustMap((action) =>
        this.sitesService.createSiteFuelProductList(action).pipe(
          map((result) => {
            return SitesActionTypes.getSiteFuelProductList({ code: action.siteCode });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.siteProductListSaved",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.siteProductListSaveFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createStoreProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createStoreProductList),
      exhaustMap((action) =>
        this.sitesService.createSiteStoreProductList(action).pipe(
          mergeMap((result) => [
            ProductActionType.getProductsList({ options: {
              paging: action.paging,
              filteringArgs: {
                filterConfig: [{ field: "code" }, { field: "description" }],
                searchTerms: action.searchTerms,
              },
              advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
            }}),
            SitesActionTypes.getSiteStoreProductList({ code: action.siteCode })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.siteProductListSaved",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.siteProductListSaveFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createGradeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createGradeList),
      exhaustMap((action) =>
        this.sitesService.createSiteGradeList(action).pipe(
          map((result) => {
            return SitesActionTypes.getSiteGradeList({ code: action.siteCode });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.siteGradeListSaved",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.siteGradeListSaveFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSitePeripheralList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSitePeripheralList),
      exhaustMap((action) =>
        this.sitesService.getSitePeripheralList(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSitePeripheralList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSitePeripheralList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadPeripheralError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getPeripheralDeviceSubTypeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getPeripheralDeviceSubtypesList),
      exhaustMap((action) =>
        this.sitesService.getDevicesSubTypes(action.pageReq, "PERIPHERAL").pipe(
          map((result) =>
            SitesActionTypes.loadPeripheralDeviceSubtypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadPeripheralDeviceSubtypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadDeviceSubTypeError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createPeripheral$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createPeripheral),
      exhaustMap((action) =>
        this.peripheralsService.createPeripheral(action.peripheral).pipe(
          map((result) => {
            return SitesActionTypes.getSitePeripheralList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PeripheralCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PeripheralFailedCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updatePeripheral$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updatePeripheral),
      exhaustMap((action) =>
        this.peripheralsService.updatePeripheral(action.peripheral).pipe(
          map((result) => {
            return SitesActionTypes.getSitePeripheralList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.PeripheralUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.PeripheralUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getSiteMultimediaList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.getSiteMultimediaList),
      exhaustMap((action) =>
        this.sitesService.getSiteMultimediaList(action.code, action.pageReq).pipe(
          map((result) =>
            SitesActionTypes.loadSiteMultimediaList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.loadSiteMultimediaList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.LoadMultimediaError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createMultimedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.createMultimedia),
      exhaustMap((action) =>
        this.multimediaService.createMultimedia(action.multimediaDevice).pipe(
          map((result) => {
            return SitesActionTypes.getSiteMultimediaList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.MultimediaCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.MultimediaFailedCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateMultimedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SitesActionTypes.updateMultimedia),
      exhaustMap((action) =>
        this.multimediaService.updateMultimedia(action.multimediaDevice).pipe(
          map((result) => {
            return SitesActionTypes.getSiteMultimediaList({
              code: result?.result?.site?.code,
              pageReq: { realSize: true, offset: 0, limit: 999 },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "SiteManagement.MultimediaUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              SitesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "SiteManagement.MultimediaUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private sitesService: SitesService,
    private warehouseService: WarehouseService,
    private dispenserService: DispenserService,
    private fccService: FccService,
    private posService: PosService,
    private fuelPointService: FuelPointService,
    private priceSignService: PriceSignService,
    private peripheralsService: PeripheralsService,
    private multimediaService: MultimediaService,
    private categoriesService: CategoriesService,
    private propertiesService: PropertiesService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  isNotFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false
  };

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
